import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Put on the trigger so a <Label htmlFor> focuses and opens it. */
  id?: string;
  /** Greys out and blocks the trigger, e.g. while a save is in flight. */
  disabled?: boolean;
  /** `lg`: touch-sized trigger, rows and a taller list (tablet forms such as audits). Implies `inlineList`. */
  size?: 'default' | 'lg';
  /**
   * Open the list in the page flow instead of floating over it. Use inside a
   * dialog: a dialog scrolls its own content and clips a floating list.
   */
  inlineList?: boolean;
}

/**
 * Searchable dropdown select component.
 * Displays a text input for filtering options with a dropdown list.
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'All',
  className = '',
  id,
  disabled = false,
  size = 'default',
  inlineList = false,
}: SearchableSelectProps) {
  const large = size === 'lg';
  const floating = !(large || inlineList);
  const text = large ? 'text-base' : 'text-sm';
  const row = large ? `px-4 py-3 ${text}` : `px-3 py-2 ${text}`;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // CR-5766: Find selected option - convert both to strings for reliable matching
  const selectedOption = options.find((o) => String(o.value) === String(value));
  const selectedLabel = selectedOption?.label || '';

  // CR-5766: Show "Loading..." when value is set but options haven't loaded yet.
  // CR-7232: fall back to the raw value when the options are loaded but none
  // matches it. The PO Headers supplier select now shares its value with the
  // toolbar search box, where users type partial terms like "Corex" that match
  // no exact option — that rendered an empty trigger sitting next to an active
  // clear button. Selects whose value always comes from their own options are
  // unaffected: selectedLabel is non-empty for them, so this never fires.
  const displayLabel = value && options.length === 0
    ? 'Loading...'
    : (selectedLabel || String(value ?? ''));

  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = useCallback((val: string) => {
    onChange(val);
    setSearch('');
    setIsOpen(false);
  }, [onChange]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
    setIsOpen(false);
  }, [onChange]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between ${large ? 'h-12 px-4' : 'px-3 py-2.5'} ${text} border border-border rounded-md bg-background text-left focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
          {value ? displayLabel : placeholder}
        </span>
        <div className="flex items-center gap-1">
          {/* CR-7774: hide the clear affordance while disabled — the button
              swallows the click, so showing an X that cannot clear is a lie. */}
          {value && !disabled && (
            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" onClick={handleClear} />
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`${floating ? 'absolute z-50 shadow-lg' : 'relative'} mt-1 w-full bg-background border border-border rounded-md ${large ? 'max-h-[min(420px,50vh)]' : 'max-h-[280px]'} flex flex-col`}>
          {/* Search input */}
          <div className={`flex items-center border-b border-border ${large ? 'px-4 py-3' : 'px-3 py-2'}`}>
            <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className={`w-full ${text} bg-transparent outline-none placeholder:text-muted-foreground`}
            />
          </div>
          {/* Options list */}
          <div className="overflow-y-auto flex-1">
            {/* CR-4766: Only inject the auto-clear row when no caller-supplied option
                already represents "no selection". Some callers prepend their own
                { value: 'all', label: 'All …' } entry whose label matches the
                placeholder — rendering both produced a duplicate "All …" row. */}
            {!options.some((o) => o.label === placeholder) && (
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`w-full text-left ${row} hover:bg-muted ${!value ? 'bg-primary/10 font-medium' : ''}`}
              >
                {placeholder}
              </button>
            )}
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={`w-full text-left ${row} hover:bg-muted ${String(opt.value) === String(value) ? 'bg-primary/10 font-medium' : ''}`}
              >
                {opt.label}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className={`${row} text-muted-foreground`}>No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
